import { EBatchImportLogStatus, TBatchImportLogResult } from './batch-import.type'

export type TBatchImportError = { message?: string; type?: string }

export enum EBatchImportTier {
  NOMINAL = 'NOMINAL',
  WARNING = 'WARNING',
  ERROR = 'ERROR',
}

export enum EBatchImportErrorCode {
  PATIENT_MISSING_IDENTITY = 'PATIENT_MISSING_IDENTITY',
  PATIENT_NOT_FOUND = 'PATIENT_NOT_FOUND',
  PATIENT_DATA_MISSING = 'PATIENT_DATA_MISSING',
  MEDEVENT_NO_FOLLOWUP = 'MEDEVENT_NO_FOLLOWUP',
  MEDEVENT_FOLLOWUP_ENDED = 'MEDEVENT_FOLLOWUP_ENDED',
  MEDEVENT_MARKED_FOR_DELETION = 'MEDEVENT_MARKED_FOR_DELETION',
  MEDEVENT_RESTORE_DIFFERENT_REMOTE_ID = 'MEDEVENT_RESTORE_DIFFERENT_REMOTE_ID',
  MEDEVENT_INVALID_DATE = 'MEDEVENT_INVALID_DATE',
  MEDEVENT_DIFFERENT_MED_EVENT_ID = 'MEDEVENT_DIFFERENT_MED_EVENT_ID',
}

// Backend messages are matched on stable fragments: some embed variable parts (e.g. the project id)
const ERROR_CODE_MATCHERS: { code: EBatchImportErrorCode; fragment: string }[] = [
  {
    code: EBatchImportErrorCode.PATIENT_MISSING_IDENTITY,
    fragment: 'missing firstname, lastname or birthdate',
  },
  { code: EBatchImportErrorCode.PATIENT_NOT_FOUND, fragment: 'no patient found matching' },
  { code: EBatchImportErrorCode.PATIENT_DATA_MISSING, fragment: 'Missing patient data in Interop payload' },
  { code: EBatchImportErrorCode.MEDEVENT_NO_FOLLOWUP, fragment: 'No mapping found for consultation data' },
  {
    code: EBatchImportErrorCode.MEDEVENT_FOLLOWUP_ENDED,
    fragment: 'PatientMedEvent found but PatientFollowup ended',
  },
  {
    code: EBatchImportErrorCode.MEDEVENT_MARKED_FOR_DELETION,
    fragment: 'Associated PatientMedEvent is marked for deletion',
  },
  {
    code: EBatchImportErrorCode.MEDEVENT_RESTORE_DIFFERENT_REMOTE_ID,
    fragment: 'Cannot restore PatientMedEvent with different remoteId',
  },
  {
    code: EBatchImportErrorCode.MEDEVENT_INVALID_DATE,
    fragment: 'Missing or invalid PatientMedEvent date',
  },
  {
    code: EBatchImportErrorCode.MEDEVENT_DIFFERENT_MED_EVENT_ID,
    fragment: 'PatientMedEvent already exists with a different MedEventId',
  },
]

export const getBatchImportErrorCode = (error: TBatchImportError): EBatchImportErrorCode | undefined =>
  ERROR_CODE_MATCHERS.find(({ fragment }) => error.message?.includes(fragment))?.code

const toError = (raw: unknown): TBatchImportError => {
  if (typeof raw === 'string') return { message: raw }
  if (raw && typeof raw === 'object') {
    const { error, message, type } = raw as Record<string, unknown>
    const text = [error, message].find((value) => typeof value === 'string')
    return {
      message: typeof text === 'string' ? text : undefined,
      type: typeof type === 'string' ? type : undefined,
    }
  }
  return {}
}

// `errors` is untyped on the backend: accept a string, an object, or an array of either
export const parseBatchImportErrors = (errors: unknown): TBatchImportError[] => {
  if (errors === undefined || errors === null) return []
  return (Array.isArray(errors) ? errors : [errors]).map(toError)
}

const PATIENT_ERROR_CODES = new Set([
  EBatchImportErrorCode.PATIENT_MISSING_IDENTITY,
  EBatchImportErrorCode.PATIENT_NOT_FOUND,
  EBatchImportErrorCode.PATIENT_DATA_MISSING,
])

// The backend `type` is authoritative; when it's absent, fall back to what the message tells us
const isMedEventError = (error: TBatchImportError): boolean => {
  if (error.type) return error.type === 'MEDEVENT'
  const code = getBatchImportErrorCode(error)
  return !!code && !PATIENT_ERROR_CODES.has(code)
}

// 3 tiers:
// - NOMINAL: no error on the interop log
// - ERROR: the line couldn't be associated to a patient (PATIENT error, or anything we can't classify)
// - WARNING: the patient is known but the appointment couldn't create/update a followup (MEDEVENT errors only)
export const getBatchImportTier = (result: TBatchImportLogResult): EBatchImportTier => {
  const errors = parseBatchImportErrors(result.errors)
  if (errors.length === 0) {
    return result.status === EBatchImportLogStatus.ERROR ? EBatchImportTier.ERROR : EBatchImportTier.NOMINAL
  }
  return errors.every(isMedEventError) ? EBatchImportTier.WARNING : EBatchImportTier.ERROR
}
